import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Cookies from 'js-cookie';
import { useHistory, useLocation } from 'react-router-dom';

import { cookieManager, SESSION_STORAGE_VARIABLES, sessionStorageManager } from '@app/services/storageManager';
import { ServerError } from '@app/components/ServerError';
import {
  getAuthUrls,
  loginFromCode,
  loginFromCookie,
  selectUser,
  sendAuthParams,
  TOKEN_INFO_COOKIE_NAME,
} from '@app/redux/auth';
import { selectConfig } from '@app/redux/config';
import { setOrigin } from '@app/redux/events';
import { retrieveAuthCode } from '@app/utils';
import { getAuthVars } from '@app/utils/getAuthVars';
import { AuthEventTypes } from '@app/types';

import { TokenProviderType } from './types';

const REDIRECT_PATH_COOKIE_NAME = 'redirectPath';

const initialFunc = (): void => {
  console.error('this function has not been set');
};

export const TokenContext = React.createContext<TokenProviderType>({
  token: null,
  isAuthenticated: false,
  isAuthMFELoaded: false,
  setToken: initialFunc,
  setIsAuthenticated: initialFunc,
  logout: initialFunc,
  login: initialFunc,
  cookieLoginFailed: false,
  redirectUrl: '',
  setLoginType: initialFunc,
});

export const WebAuthProvider = ({ children }: { children: React.ReactNode }): JSX.Element => {
  const [showAuthError, setShowAuthError] = useState(false);
  const { platform, env: mvEnv, mfeEnv, locale } = getAuthVars();

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(
      sendAuthParams({
        env: mvEnv,
        platform,
        mfeEnv,
        locale,
      })
    );
    dispatch(setOrigin({ origin: window.location.origin }));
  }, [dispatch, mvEnv, platform, mfeEnv, locale]);

  const {
    user: {
      auth: { tokenInfo, isAuthenticated, isAuthMFELoaded },
    },
  } = useSelector(selectUser);

  const {
    auth: { client, baseUrl },
  } = useSelector(selectConfig);

  const logout = () => {
    setShowAuthError(false);
    const { logoutUrl } = getAuthUrls(client, baseUrl, tokenInfo.idToken);
    cookieManager.deleteAll().finally((): void => {
      [
        SESSION_STORAGE_VARIABLES.DISMISSED_ALERTS,
        SESSION_STORAGE_VARIABLES.CHANGE_PLAN_SUCCESS,
        SESSION_STORAGE_VARIABLES.PURCHASED_PRODUCT_TYPE_ID,
      ].forEach((item) => {
        sessionStorageManager.removeItem(item);
      });

      window.location.assign(logoutUrl);
    });
  };

  useEffect(() => {
    const handleMessage = (message: any): any => {
      const messageData = message.data;
      if (messageData.domain === 'General' && messageData.eventType === AuthEventTypes.LoginEvent) {
        const isReloaded =
          performance?.getEntriesByType('navigation')?.length &&
          (performance?.getEntriesByType('navigation')[0] as PerformanceNavigationTiming)?.type === 'reload';

        if (isReloaded) {
          return window.removeEventListener('message', handleMessage);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const login = (url?: string) => {
    if (url) {
      const code = retrieveAuthCode(url, client.redirectUrl ?? '') ?? '';
      if (code) {
        dispatch(loginFromCode({ code, locale }));

        return;
      }
    }
    cookieManager.get(TOKEN_INFO_COOKIE_NAME).then((stringifiedTokenInfo) => {
      dispatch(loginFromCookie({ stringifiedTokenInfo: stringifiedTokenInfo ?? '' }));
    });
  };

  return (
    <TokenContext.Provider
      value={{
        token: tokenInfo,
        logout,
        setIsAuthenticated: () => undefined,
        setToken: () => undefined,
        isAuthenticated,
        isAuthMFELoaded,
        cookieLoginFailed: false,
        redirectUrl: client.redirectUrl ?? undefined,
        loginUrl: '',
        login,
      }}
    >
      {showAuthError ? <ServerError /> : children}
    </TokenContext.Provider>
  );
};

const useSetRedirectPath = ({
  isAuthenticated,
  redirectUrl,
}: {
  isAuthenticated: boolean;
  redirectUrl?: string | null;
}): void => {
  const { pathname } = useLocation();

  useEffect((): void => {
    if (isAuthenticated) return;

    const redirectPath = Cookies.get(REDIRECT_PATH_COOKIE_NAME);
    if (!redirectPath) {
      Cookies.set(REDIRECT_PATH_COOKIE_NAME, pathname);
    }
  }, [pathname, redirectUrl, isAuthenticated]);
};

const useGoToRedirectPath = ({
  isAuthenticated,
  redirectUrl,
}: {
  isAuthenticated: boolean;
  redirectUrl?: string | null;
}): void => {
  const history = useHistory();

  useEffect(() => {
    if (!isAuthenticated || !redirectUrl) return;

    const path = Cookies.get(REDIRECT_PATH_COOKIE_NAME);
    Cookies.remove(REDIRECT_PATH_COOKIE_NAME);

    const url = new URL(redirectUrl);
    const isSavedPathSameAsAuthRedirectPath = url.pathname === path;
    if (isSavedPathSameAsAuthRedirectPath) {
      history.push('/');
    } else {
      history.push(path ?? '/');
    }
  }, [history, redirectUrl, isAuthenticated]);
};

export const useRedirectPath = (): void => {
  const {
    user: { auth },
  } = useSelector(selectUser);
  const { isAuthenticated } = auth;
  const {
    auth: { client },
  } = useSelector(selectConfig);
  const { redirectUrl } = client;

  useSetRedirectPath({ isAuthenticated, redirectUrl });
  useGoToRedirectPath({ isAuthenticated, redirectUrl });
};
